import { CaretDownIcon } from '@st/theme'
import { useProcess, useSelect } from '~/store'

type DocumentNavigationState = {
  currentPage: number
  pageCount: number
  prevPage: number | undefined
  nextPage: number | undefined
}

type FloatingPageChipProps = {
  navigation: DocumentNavigationState
  onRequestPage: (pageNumber: number) => void
}

function selDocumentNavigationState(viewerState: {
  pagesStatus: 'loading' | 'loaded' | 'failed'
  pages: { length: number }
  viewportState: { activePageNumber: number | null }
  viewer: { viewportMeasurement: unknown | null }
}): DocumentNavigationState | null {
  const currentPage = viewerState.viewportState.activePageNumber
  const pageCount = viewerState.pages.length

  if (
    viewerState.pagesStatus != 'loaded' ||
    viewerState.viewer.viewportMeasurement == null ||
    pageCount <= 1 ||
    currentPage == null
  ) {
    return null
  }

  return {
    currentPage,
    pageCount,
    prevPage: currentPage > 1 ? currentPage - 1 : undefined,
    nextPage: currentPage < pageCount ? currentPage + 1 : undefined
  }
}

export function FloatingPageChip({ navigation, onRequestPage }: FloatingPageChipProps) {
  return (
    <div className="pointer-events-auto inline-flex h-8 items-center rounded-lg bg-stone-800 px-1 leading-4 text-gray-200">
      <button
        type="button"
        aria-label="Previous page"
        disabled={navigation.prevPage == undefined}
        className="flex items-center justify-center rounded p-1 text-gray-400 transition-colors hover:text-gray-200 disabled:cursor-default disabled:opacity-50 disabled:hover:text-gray-700"
        onClick={() => {
          if (navigation.prevPage != undefined) {
            onRequestPage(navigation.prevPage)
          }
        }}
      >
        <CaretDownIcon className="size-4 rotate-90" />
      </button>
      <div className="w-14 text-center text-sm tabular-nums">
        {navigation.currentPage} / {navigation.pageCount}
      </div>
      <button
        type="button"
        aria-label="Next page"
        disabled={navigation.nextPage == undefined}
        className="flex items-center justify-center rounded p-1 text-gray-400 transition-colors hover:text-gray-200 disabled:cursor-default disabled:opacity-50 disabled:hover:text-gray-700"
        onClick={() => {
          if (navigation.nextPage != undefined) {
            onRequestPage(navigation.nextPage)
          }
        }}
      >
        <CaretDownIcon className="size-4 -rotate-90" />
      </button>
    </div>
  )
}

export function ViewerPaginationChipOverlay() {
  const stFolder = useProcess('stFolder')
  const navigation = useSelect('stFolder', (state) => selDocumentNavigationState(state.viewer))

  if (!navigation) {
    return null
  }

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
      <FloatingPageChip
        navigation={navigation}
        onRequestPage={(pageNumber) => {
          stFolder.send({
            type: 'viewer',
            message: {
              type: 'viewer',
              message: {
                type: 'requestViewerScroll',
                target: { pageNumber },
                behavior: 'smooth'
              }
            }
          })
        }}
      />
    </div>
  )
}
